import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Broadcast, MapPin } from "@phosphor-icons/react";

export default function GoLiveDialog({ open, onOpenChange, job, onDone }) {
  const [radius, setRadius] = useState(job?.max_radius_km || 25);
  const [lat, setLat] = useState(job?.lat || "");
  const [lon, setLon] = useState(job?.lon || "");
  const [busy, setBusy] = useState(false);

  const useGeolocation = () => {
    if (!navigator.geolocation) { toast.error("Location unsupported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLon(pos.coords.longitude.toFixed(6)); toast.success("Picked up your location"); },
      (err) => toast.error(err.message)
    );
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { max_radius_km: Number(radius) };
      if (lat && lon) { body.lat = Number(lat); body.lon = Number(lon); }
      const r = await api.post(`/company/jobs/${job.id}/go-live`, body);
      toast.success(r.data?.offer_id ? "Job is live — offer sent!" : "Job is live");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not go live");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="go-live-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Broadcast className="text-emerald-600" weight="fill"/> Go live — <span className="truncate">{job?.title}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">The smart matcher will broadcast this shift to the best-matching worker in real time. If they don't accept in 30s, the next best worker gets it.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lat">Latitude</Label>
              <Input id="lat" data-testid="go-live-lat" value={lat} onChange={(e)=>setLat(e.target.value)} placeholder="auto from postcode"/>
            </div>
            <div>
              <Label htmlFor="lon">Longitude</Label>
              <Input id="lon" data-testid="go-live-lon" value={lon} onChange={(e)=>setLon(e.target.value)} placeholder="auto from postcode"/>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={useGeolocation}><MapPin size={14} className="mr-1.5"/>Use my location</Button>
          <div>
            <Label htmlFor="radius">Max radius (km)</Label>
            <Input id="radius" data-testid="go-live-radius" type="number" min="0.5" max="200" step="0.5" value={radius} onChange={(e)=>setRadius(e.target.value)} />
            <div className="text-xs text-slate-500 mt-1">Workers further than this won't be considered.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="go-live-submit" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">{busy ? "Going live…" : "Go live"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
